import { PageLayout } from '@/components/layout'
import { Footer } from '@/components/layout/Footer'
import { useAuth } from '@/hooks/useAuth'
import { useDashboardOverview } from '@/hooks/useDashboard'
import { LoadingSpinner } from '@/components/common'

export const Profile = () => {
  const { user } = useAuth()
  const { data: dashboardData, isLoading, error } = useDashboardOverview(user?.organizationId)

  if (isLoading) {
    return (
      <PageLayout showHeader={true}>
        <div className="p-6 md:p-8 lg:p-10 flex items-center justify-center min-h-[400px]">
          <LoadingSpinner size="lg" />
        </div>
      </PageLayout>
    )
  }

  if (error) {
    return (
      <PageLayout showHeader={true}>
        <div className="p-6 md:p-8 lg:p-10">
          <div className="max-w-4xl mx-auto">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-red-600 dark:text-red-400">
                Error loading profile: {error instanceof Error ? error.message : 'Unknown error'}
              </p>
            </div>
          </div>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout showHeader={true}>
      <div className="p-6 md:p-8 lg:p-10">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-text-main-light dark:text-text-main-dark mb-2">
              Account Information
            </h1>
            <p className="text-text-sub-light dark:text-text-sub-dark">
              View and manage your account details and organization information.
            </p>
          </div>

          {/* User Details Card */}
          <div className="mb-6">
            <div className="bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-6 text-text-main-light dark:text-text-main-dark flex items-center gap-2">
                <span className="material-symbols-outlined">person</span>
                User Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium text-text-sub-light dark:text-text-sub-dark mb-1 block">
                    Full Name
                  </label>
                  <p className="text-text-main-light dark:text-text-main-dark">
                    {user?.name || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-text-sub-light dark:text-text-sub-dark mb-1 block">
                    Email Address
                  </label>
                  <p className="text-text-main-light dark:text-text-main-dark">
                    {user?.email || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-text-sub-light dark:text-text-sub-dark mb-1 block">
                    Account Status
                  </label>
                  <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                    user?.is_active !== false
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                  }`}>
                    <span className="material-symbols-outlined text-sm">check_circle</span>
                    {user?.is_active !== false ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div>
                  <label className="text-sm font-medium text-text-sub-light dark:text-text-sub-dark mb-1 block">
                    Email Verified
                  </label>
                  <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                    user?.email_verified
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                  }`}>
                    <span className="material-symbols-outlined text-sm">
                      {user?.email_verified ? 'verified' : 'warning'}
                    </span>
                    {user?.email_verified ? 'Verified' : 'Not Verified'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Organization Details Card */}
          {dashboardData && (
            <div className="mb-6">
              <div className="bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg p-6">
                <h2 className="text-xl font-semibold mb-6 text-text-main-light dark:text-text-main-dark flex items-center gap-2">
                  <span className="material-symbols-outlined">groups</span>
                  Organization
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm font-medium text-text-sub-light dark:text-text-sub-dark mb-1 block">
                      Organization Name
                    </label>
                    <p className="text-text-main-light dark:text-text-main-dark">
                      {dashboardData.organization_name}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-text-sub-light dark:text-text-sub-dark mb-1 block">
                      Organization ID
                    </label>
                    <p className="text-text-main-light dark:text-text-main-dark font-mono text-sm">
                      {dashboardData.organization_id}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-text-sub-light dark:text-text-sub-dark mb-1 block">
                      Total Members
                    </label>
                    <p className="text-text-main-light dark:text-text-main-dark">
                      {dashboardData.member_count}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-text-sub-light dark:text-text-sub-dark mb-1 block">
                      Subscriptions
                    </label>
                    <p className="text-text-main-light dark:text-text-main-dark">
                      {dashboardData.active_subscriptions} active / {dashboardData.total_subscriptions} total
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Info Message */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <p className="text-sm text-text-sub-light dark:text-text-sub-dark flex items-start gap-2">
              <span className="material-symbols-outlined text-sm mt-0.5">info</span>
              <span>
                To update your account information or organization settings, please contact your administrator or reach out to support.
              </span>
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </PageLayout>
  )
}

